(() => {
  if (window.__shpfltComplementaryProductsLoaded) return;
  window.__shpfltComplementaryProductsLoaded = true;

  if (customElements.get('shpflt-complementary-products')) return;

  class ShpfltComplementaryProducts extends HTMLElement {
    constructor() {
      super();
      this.controller = null;
      this.rafId = 0;
      this.cartChangeRafId = 0;
      this.onCartChangeDebounced = this.onCartChangeDebounced.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPrev = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
    }

    connectedCallback() {
      this.prevButton = this.querySelector('[data-shpflt-complementary-prev]');
      this.nextButton = this.querySelector('[data-shpflt-complementary-next]');
      this.cacheRow();
      this.tracksCart = this.classList.contains('shpflt-complementary--drawer');

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      if (this.tracksCart) {
        document.addEventListener('theme:cart:change', this.onCartChangeDebounced);
      }

      this.updateButtons();

      if (!this.row) {
        this.fetchInitial();
      }
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      if (this.tracksCart) {
        document.removeEventListener('theme:cart:change', this.onCartChangeDebounced);
      }
      this.row?.removeEventListener('scroll', this.onScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.cartChangeRafId) cancelAnimationFrame(this.cartChangeRafId);
      this.controller?.abort();
    }

    onCartChangeDebounced() {
      if (this.cartChangeRafId) return;
      this.cartChangeRafId = requestAnimationFrame(() => {
        this.cartChangeRafId = 0;
        this.onCartChange();
      });
    }

    cacheRow() {
      const next = this.querySelector('[data-shpflt-complementary-row]');
      if (this.row && this.row !== next) {
        this.row.removeEventListener('scroll', this.onScroll);
      }
      this.row = next;
      if (this.row) this.row.addEventListener('scroll', this.onScroll, { passive: true });
    }

    get reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    scrollByCard(direction) {
      if (!this.row) return;
      const firstItem = this.row.querySelector('.shpflt-complementary__item');
      if (!firstItem) return;
      const rowStyles = window.getComputedStyle(this.row);
      const gap = parseFloat(rowStyles.columnGap || rowStyles.gap || '0') || 0;
      const step = firstItem.getBoundingClientRect().width + gap;
      this.row.scrollBy({
        left: direction * step,
        behavior: this.reduceMotion ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.updateButtons();
      });
    }

    updateButtons() {
      if (!this.row || !this.prevButton || !this.nextButton) return;
      const max = this.row.scrollWidth - this.row.clientWidth;
      const atStart = this.row.scrollLeft <= 1;
      const atEnd = this.row.scrollLeft >= max - 1;
      const noOverflow = max <= 1;
      this.prevButton.disabled = atStart || noOverflow;
      this.nextButton.disabled = atEnd || noOverflow;
    }

    async fetchHtml(url) {
      try {
        const response = await fetch(url.toString(), { signal: this.controller.signal });
        if (!response.ok) return null;
        return await response.text();
      } catch (error) {
        if (error.name !== 'AbortError') return null;
        throw error;
      }
    }

    swapHtml(html) {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshComponent = doc.querySelector('shpflt-complementary-products');
      const freshContent = doc.querySelector('[data-api-content]');
      const hasProducts = !!doc.querySelector('[data-shpflt-complementary-row]');
      if (!hasProducts) return false;

      const current = this.querySelector('[data-api-content]');
      if (current && freshContent) {
        current.replaceWith(freshContent);
      } else if (freshComponent) {
        // Initial render had no chrome (rec_products was empty server-side).
        // Inject header + data-api-content together.
        this.innerHTML = freshComponent.innerHTML;
        this.prevButton = this.querySelector('[data-shpflt-complementary-prev]');
        this.nextButton = this.querySelector('[data-shpflt-complementary-next]');
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);
      } else {
        return false;
      }

      this.cacheRow();
      this.updateButtons();
      this.show();
      this.refreshLazyImages();
      return true;
    }

    refreshLazyImages() {
      this.querySelectorAll('[data-aos]').forEach((el) => {
        for (const name of Object.keys(el.dataset)) {
          if (name.startsWith('aos')) delete el.dataset[name];
        }
        el.classList.add('aos-init', 'aos-animate');
      });
      if (typeof window.AOS !== 'undefined' && typeof window.AOS.refresh === 'function') {
        window.AOS.refresh();
      }

      const lazyEls = this.querySelectorAll('.lazyload');
      if (typeof window.lazySizes !== 'undefined' && window.lazySizes.loader?.unveil) {
        lazyEls.forEach((el) => window.lazySizes.loader.unveil(el));
        return;
      }
      lazyEls.forEach((el) => {
        const src = el.dataset.src;
        const bgset = el.dataset.bgset;
        if (src && el.tagName === 'IMG') el.src = src;
        if (bgset) {
          const first = bgset.split(',')[0]?.trim().split(' ')[0];
          if (first) el.style.backgroundImage = `url('${first}')`;
        }
        el.classList.remove('lazyload');
        el.classList.add('lazyloaded');
      });
    }

    get wrapper() {
      return this.closest('[data-cart-drawer-complementary]');
    }

    show() {
      this.hidden = false;
      const wrapper = this.wrapper;
      if (wrapper) wrapper.hidden = false;
    }

    buildRecsUrl(intentOverride) {
      const recsUrl = this.dataset.productRecommendationsUrl;
      const productId = this.dataset.sourceProductId;
      if (!recsUrl || !productId) return null;
      const intent = this.dataset.intent || intentOverride || 'complementary';
      const url = new URL(recsUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionHandle || 'shpflt-complementary-products');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('intent', intent);
      url.searchParams.set('limit', this.dataset.limit || '8');
      return url;
    }

    async fetchInitial() {
      this.controller?.abort();
      this.controller = new AbortController();

      try {
        const html = await this.fetchHtml(this.buildRecsUrl());
        if (this.swapHtml(html)) return;
        this.hide();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hide();
        }
      }
    }

    hide() {
      this.hidden = true;
      const wrapper = this.wrapper;
      if (wrapper) wrapper.hidden = true;
    }

    async getLatestCartProductId() {
      try {
        const response = await fetch('/cart.js', { signal: this.controller.signal });
        if (!response.ok) return null;
        const cart = await response.json();
        const id = cart.items?.[0]?.product_id;
        return id ? String(id) : null;
      } catch (error) {
        if (error.name !== 'AbortError') return null;
        throw error;
      }
    }

    async onCartChange() {
      this.controller?.abort();
      this.controller = new AbortController();

      try {
        const latestId = await this.getLatestCartProductId();
        if (!latestId) {
          this.hide();
          return;
        }
        this.dataset.sourceProductId = latestId;
        await this.fetchInitial();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hide();
        }
      }
    }
  }

  customElements.define('shpflt-complementary-products', ShpfltComplementaryProducts);
})();
